import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * دوال مساعدة لاختبار نظام التكلفة والدفع في الصيانة
 */

/**
 * محاكاة حالة "انتظار موافقة العميل"
 * تستخدم لاختبار النظام الجديد
 */
export const simulateCustomerApprovalRequired = async (
  maintenanceId: string,
  estimatedCost: number = 500
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    
    await updateDoc(maintenanceRef, {
      // تعيين التكلفة المقدرة
      estimatedCost,
      
      // تفعيل طلب الاتصال بالعميل
      requireCallBeforeMaintenance: true,
      
      // تم إشعار العميل لكن لم يوافق بعد
      customerNotified: true,
      customerApproved: false,
      
      // تحديث التاريخ
      updatedAt: Timestamp.now(),
      
      // إضافة ملاحظة للاختبار
      testNote: 'تم تفعيل حالة انتظار موافقة العميل للاختبار'
    });
    
    console.log(`✅ تم تفعيل حالة "انتظار موافقة العميل" لطلب الصيانة ${maintenanceId}`);
    console.log(`💰 التكلفة المقدرة: ${estimatedCost} جنيه`);
  } catch (error) {
    console.error('❌ خطأ في محاكاة حالة انتظار موافقة العميل:', error);
    throw error;
  }
};

/**
 * إعادة تعيين حالة الصيانة للاختبار
 */
export const resetMaintenanceForTesting = async (
  maintenanceId: string
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    
    await updateDoc(maintenanceRef, {
      // إعادة تعيين الحقول الجديدة
      estimatedCost: 0,
      finalCost: 0,
      partsCost: 0,
      laborCost: 0,
      totalPaid: 0,
      remainingAmount: 0,
      hasDebt: false,
      
      // إعادة تعيين حالة العميل
      requireCallBeforeMaintenance: false,
      customerNotified: false,
      customerApproved: false,
      
      // إعادة تعيين الحالة
      status: 'pending',
      
      // تحديث التاريخ
      updatedAt: Timestamp.now(),
      
      // حذف ملاحظة الاختبار
      testNote: null
    });
    
    console.log(`🔄 تم إعادة تعيين طلب الصيانة ${maintenanceId} للاختبار`);
  } catch (error) {
    console.error('❌ خطأ في إعادة تعيين طلب الصيانة:', error);
    throw error;
  }
};

/**
 * إنشاء سيناريو اختبار كامل
 */
export const createTestScenario = async (
  maintenanceId: string,
  scenario: 'approval_required' | 'approved' | 'rejected' | 'reset'
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    
    switch (scenario) {
      case 'approval_required':
        await updateDoc(maintenanceRef, {
          initialCost: 300,
          estimatedCost: 450,
          requireCallBeforeMaintenance: true,
          customerNotified: true,
          customerApproved: false,
          status: 'pending',
          updatedAt: Timestamp.now(),
          testScenario: 'انتظار موافقة العميل'
        });
        console.log('📞 تم إنشاء سيناريو: انتظار موافقة العميل');
        break;
        
      case 'approved':
        await updateDoc(maintenanceRef, {
          initialCost: 300,
          estimatedCost: 450,
          finalCost: 450,
          requireCallBeforeMaintenance: true,
          customerNotified: true,
          customerApproved: true,
          status: 'inProgress',
          updatedAt: Timestamp.now(),
          testScenario: 'تم الموافقة'
        });
        console.log('✅ تم إنشاء سيناريو: تم الموافقة');
        break;
        
      case 'rejected':
        await updateDoc(maintenanceRef, {
          initialCost: 300,
          estimatedCost: 450,
          requireCallBeforeMaintenance: true,
          customerNotified: true,
          customerApproved: false,
          status: 'cancelled',
          cancelledAt: Timestamp.now(),
          cancellationReason: 'رفض العميل للتكلفة المقدرة',
          updatedAt: Timestamp.now(),
          testScenario: 'تم الرفض'
        });
        console.log('❌ تم إنشاء سيناريو: تم الرفض');
        break;
        
      case 'reset':
        await resetMaintenanceForTesting(maintenanceId);
        break;
    }
  } catch (error) {
    console.error('❌ خطأ في إنشاء سيناريو الاختبار:', error);
    throw error;
  }
};

/**
 * طباعة تعليمات الاختبار
 */
export const printTestInstructions = (maintenanceId: string): void => {
  console.log(`
🧪 تعليمات اختبار نظام التكلفة والدفع
=====================================

لاختبار النظام، استخدم الدوال التالية في وحدة تحكم المتصفح:

1️⃣ تفعيل حالة "انتظار موافقة العميل":
   simulateCustomerApprovalRequired('${maintenanceId}', 500)

2️⃣ إنشاء سيناريوهات مختلفة:
   createTestScenario('${maintenanceId}', 'approval_required')
   createTestScenario('${maintenanceId}', 'approved')
   createTestScenario('${maintenanceId}', 'rejected')

3️⃣ إعادة تعيين للاختبار:
   createTestScenario('${maintenanceId}', 'reset')

📋 خطوات الاختبار:
1. انتقل إلى صفحة تفاصيل الصيانة
2. شغل أحد السيناريوهات أعلاه
3. أعد تحميل الصفحة لرؤية التغييرات
4. اختبر الأزرار والنوافذ الجديدة

💡 ملاحظة: تأكد من استبدال '${maintenanceId}' بمعرف طلب صيانة حقيقي
  `);
};

// تصدير الدوال للاستخدام العام
if (typeof window !== 'undefined') {
  (window as any).simulateCustomerApprovalRequired = simulateCustomerApprovalRequired;
  (window as any).resetMaintenanceForTesting = resetMaintenanceForTesting;
  (window as any).createTestScenario = createTestScenario;
  (window as any).printTestInstructions = printTestInstructions;
}
